import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play, ShieldCheck, Zap, Server } from "lucide-react";
import { Button } from "./ui/button";
import { stats } from "../mock";
import ParticleSphere from "./ParticleSphere";
import TechBackdrop from "./TechBackdrop";
import CodeWindow from "./CodeWindow";

export default function Hero() {
  return (
    <section className="relative pt-32 lg:pt-40 pb-16 lg:pb-24 overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-70" />
      {/* Tech circuit graphic in background */}
      <TechBackdrop variant="circuit" opacity={0.08} className="hidden lg:block" />
      <div className="absolute -top-40 -left-32 w-[40rem] h-[40rem] rounded-full bg-gradient-to-br from-brand-200/50 via-accentc-300/30 to-transparent blur-3xl animate-drift" />
      <div className="absolute top-20 -right-20 w-[32rem] h-[32rem] rounded-full bg-gradient-to-br from-accentc-300/40 to-transparent blur-3xl animate-drift [animation-delay:-6s]" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-200 bg-brand-50/70 backdrop-blur">
              <span className="relative flex items-center justify-center">
                <span className="absolute h-2 w-2 rounded-full bg-brand-500 animate-pulse-ring" />
                <span className="h-2 w-2 rounded-full bg-brand-500" />
              </span>
              <span className="text-[12px] font-medium text-brand-800 tracking-wide">
                IBM Business Partner  ·  Established 2013
              </span>
            </div>

            <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-[64px] lg:leading-[1.05] font-bold text-slate-900">
              Enterprise
              <span className="block">
                <span className="shimmer-text">AIX & Power Systems</span>
              </span>
              consulting, done right.
            </h1>

            <p className="mt-6 text-[18px] leading-relaxed text-slate-600 max-w-xl">
              Deep-specialist engineers for <b>PowerVM virtualization</b>, <b>PowerHA clustering</b>, <b>AIX administration</b> and <b>storage</b>. Predictable projects. Rock-solid support. Real knowledge transfer.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact">
                <Button className="rounded-full h-12 px-6 bg-brand-700 hover:bg-brand-800 text-white shadow-md shadow-brand-900/10">
                  Book a free consult <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" className="rounded-full h-12 px-6 border-slate-300 hover:border-brand-500 hover:text-brand-700">
                  <Play className="mr-2 h-4 w-4" /> See our services
                </Button>
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap gap-5 text-[13px] text-slate-600">
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-600"/>ITIL-aligned change management</div>
              <div className="flex items-center gap-2"><Zap className="h-4 w-4 text-brand-600"/>15-min response (24×7 plan)</div>
              <div className="flex items-center gap-2"><Server className="h-4 w-4 text-brand-600"/>AIX 4.3 → 7.3 supported</div>
            </div>

            {/* Live AIX terminal snippet */}
            <div className="hidden lg:block mt-10 max-w-[440px]">
              <CodeWindow />
            </div>
          </div>

          {/* Animated particle sphere visual inside dark cosmic frame */}
          <div className="lg:col-span-5">
            <div className="relative aspect-square w-full max-w-[520px] mx-auto">
              {/* Soft outer glow */}
              <div className="absolute -inset-6 rounded-[36px] bg-gradient-to-br from-brand-300/40 via-accentc-300/20 to-transparent blur-2xl animate-drift" />

              {/* Dark cosmic frame */}
              <div className="relative h-full w-full rounded-[32px] overflow-hidden border border-slate-800/80 shadow-2xl shadow-brand-900/20 bg-[radial-gradient(ellipse_at_center,#0b2a3d_0%,#050e1a_55%,#02060d_100%)]">
                {/* Star field backdrop */}
                <svg className="absolute inset-0 w-full h-full opacity-60" aria-hidden="true">
                  <defs>
                    <pattern id="stars" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                      <circle cx="6"  cy="10" r="0.6" fill="hsl(var(--accentc-300))" opacity=".6" />
                      <circle cx="26" cy="22" r="0.4" fill="hsl(var(--brand-300))" opacity=".5" />
                      <circle cx="14" cy="34" r="0.5" fill="hsl(var(--brand-200))" opacity=".4" />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#stars)" />
                </svg>

                {/* Subtle grid */}
                <div className="absolute inset-0 opacity-[0.12]"
                  style={{
                    backgroundImage:
                      "linear-gradient(hsl(var(--brand-300) / .4) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--brand-300) / .4) 1px, transparent 1px)",
                    backgroundSize: "36px 36px"
                  }}
                />

                {/* Concentric rings */}
                <div className="absolute inset-10 rounded-full border border-brand-400/10" />
                <div className="absolute inset-20 rounded-full border border-brand-400/15" />

                {/* Rotating particle sphere — follows active theme */}
                <ParticleSphere size={520} count={150} />

                {/* Orbiting badge */}
                <div className="absolute inset-0 animate-slow-spin pointer-events-none">
                  <div className="absolute top-6 left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-accentc-300 shadow-[0_0_16px_hsl(var(--accentc-300))]" />
                </div>
                <div className="absolute inset-0 animate-slow-spin [animation-duration:90s] [animation-direction:reverse] pointer-events-none">
                  <div className="absolute bottom-10 left-1/2 -translate-x-1/2 h-2.5 w-2.5 rounded-full bg-brand-300 shadow-[0_0_12px_hsl(var(--brand-300))]" />
                </div>

                {/* Coordinates HUD */}
                <div className="absolute top-4 left-4 text-[10px] font-mono text-brand-300/80 tracking-[0.18em]">
                  LAT  −26.10  ·  LON  +28.05
                </div>
                <div className="absolute top-4 right-4 flex items-center gap-1.5 text-[10px] font-mono text-accentc-300/80 tracking-[0.18em]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> ONLINE
                </div>
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-[10px] font-mono text-brand-300/70 tracking-[0.18em]">
                  <span>NODE  A:ACTIVE · B:STANDBY</span>
                  <span>HB 3.2ms</span>
                </div>
              </div>

              {/* Floating stat cards - stay on light bg */}
              <div className="hidden md:block animate-float absolute -left-6 top-10 glass rounded-2xl p-4 shadow-xl">
                <div className="text-[11px] uppercase tracking-widest text-brand-700">Cluster status</div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-sm font-semibold text-slate-800">node-a · active</span>
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-brand-500" />
                  <span className="text-sm font-semibold text-slate-800">node-b · standby</span>
                </div>
              </div>

              <div className="hidden md:block animate-float [animation-delay:1.2s] absolute -right-4 bottom-14 glass rounded-2xl p-4 shadow-xl w-[200px]">
                <div className="text-[11px] uppercase tracking-widest text-brand-700">LPAR load</div>
                <div className="mt-2 flex items-end gap-1 h-10">
                  {[40,60,55,72,48,80,66,70,58,82].map((v,i)=> (
                    <div
                      key={i}
                      className="flex-1 rounded-sm bg-gradient-to-t from-brand-600 to-brand-300"
                      style={{
                        height: `${v}%`,
                        animation: `bar-breathe 2.4s ease-in-out ${i * 0.12}s infinite alternate`
                      }}
                    />
                  ))}
                </div>
                <div className="mt-2 text-xs text-slate-600">CPU entitlement: 3.2 / 4.0</div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <div key={i} className="gradient-border p-6 hover-lift">
              <div className="font-display text-3xl lg:text-4xl font-bold text-slate-900">{s.value}</div>
              <div className="mt-1 text-[13px] text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* keyframes for LPAR bar breathe (kept local) */}
      <style>{`
        @keyframes bar-breathe {
          0%   { transform: scaleY(0.78); }
          100% { transform: scaleY(1);    }
        }
      `}</style>
    </section>
  );
}
